<script lang="ts">
  import { p, route, isActive } from '../utils/router';

  interface Crumb {
    label: string;
    href: string;
  }

  interface Props {
    /** Override the final crumb label (e.g., participant name) */
    currentLabel?: string;
  }

  let { currentLabel }: Props = $props();

  const params = $derived(route.params as { id?: string });

  const crumbs = $derived.by((): Crumb[] => {
    const result: Crumb[] = [];

    // Always start with dashboard (which is the participants overview)
    result.push({ label: 'Dashboard', href: p('/') });

    // /participants is the edit/manage list - sibling to participant stats, not parent
    if (isActive.startsWith('/participants/new')) {
      result.push({
        label: 'Rediger deltakere',
        href: p('/participants/edit'),
      });
      result.push({ label: 'Ny deltaker', href: p('/participants/new') });
    } else if (isActive.startsWith('/participants/json')) {
      result.push({
        label: 'Rediger deltakere',
        href: p('/participants/edit'),
      });
      result.push({ label: 'JSON', href: p('/participants/json') });
    } else if (isActive.startsWith('/participants/:id/edit')) {
      // /participants/:id/edit - editing a specific participant
      if (currentLabel && params.id) {
        result.push({
          label: currentLabel,
          href: p('/participants/:id', { params: { id: params.id } }),
        });
        result.push({ label: 'Rediger', href: '#' });
      }
    } else if (isActive.startsWith('/participants/:id')) {
      // /participants/:id (stats page) - directly under dashboard
      if (currentLabel) {
        result.push({ label: currentLabel, href: '#' });
      }
    } else if (isActive.startsWith('/participants/edit')) {
      // /participants/edit - the edit/manage list
      result.push({
        label: 'Rediger deltakere',
        href: p('/participants/edit'),
      });
    } else if (isActive.startsWith('/groups/:id')) {
      // /groups/:id - group detail
      result.push({ label: 'Grupper', href: p('/groups') });
      if (currentLabel) {
        result.push({ label: currentLabel, href: '#' });
      }
    } else if (isActive.startsWith('/groups')) {
      result.push({ label: 'Grupper', href: p('/groups') });
    } else if (isActive.startsWith('/game-info')) {
      result.push({ label: 'Spillinfo', href: p('/game-info') });
    }

    return result;
  });
</script>

{#if crumbs.length > 1}
  <nav aria-label="Brødsmuler" class="breadcrumb">
    <ol>
      {#each crumbs as crumb, i (i)}
        <li>
          {#if i === crumbs.length - 1}
            <span aria-current="page">{crumb.label}</span>
          {:else}
            <a href={crumb.href}>{crumb.label}</a>
          {/if}
        </li>
      {/each}
    </ol>
  </nav>
{/if}

<style>
  .breadcrumb {
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  ol {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-1);
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  li:not(:last-child)::after {
    content: '/';
    color: var(--text-muted);
    opacity: 0.5;
  }

  a {
    color: var(--text-muted);
    text-decoration: none;
    transition: color var(--duration-fast) var(--ease);
  }

  a:hover {
    color: var(--accent);
  }

  span[aria-current='page'] {
    color: var(--text);
    font-weight: var(--nrk-sans-weight-medium);
  }
</style>
